import React, { useEffect, useMemo, useState } from 'react';
import { tournamentService, uploadService } from '../services/api';

const empty = {
  name: '', game_id: '', package_category_id: '', format: 'teams',
  bracket_type: 'single_elimination', team_limit: 8, players_per_team: 5,
  reserve_players: 1, registration_fee: '100.00', registration_starts_at: '',
  registration_ends_at: '', starts_at: '', ends_at: '', early_best_of: 1,
  semifinal_best_of: 3, final_best_of: 5, third_place_enabled: true,
  third_place_best_of: 3, prize_first: '', prize_second: '', prize_third: '',
  status: 'draft'
};

const dt = v => v ? String(v).slice(0, 16) : '';
const statusLabel = {
  draft: 'Rascunho · Inscrições Desativadas',
  registration: '● Inscrições Abertas',
  ready: 'Chaveamento Pronto',
  running: 'Em Andamento',
  finished: 'Finalizado',
  cancelled: 'Cancelado'
};

export default function Tournaments() {
  const [list, setList] = useState([]);
  const [opts, setOpts] = useState({ games: [], categories: [], clients: [], clans: [] });
  const [form, setForm] = useState(null);
  const [teamForm, setTeamForm] = useState(null);
  const [awardForm, setAwardForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');

  const load = async () => {
    try {
      const [a, b] = await Promise.all([tournamentService.getAll(), tournamentService.options()]);
      setList(a.data);
      setOpts(b.data);
    } catch (e) {
      console.error('Erro ao carregar campeonatos:', e);
    }
  };

  useEffect(() => { load(); }, []);

  const saveTournament = async () => {
    setBusy(true);
    try {
      const data = { ...form };
      if (form.id) await tournamentService.update(form.id, data);
      else await tournamentService.create(data);
      setForm(null);
      await load();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleRegistrations = async (t, active) => {
    const action = active ? 'abrir' : 'desativar';
    if (!window.confirm(`Deseja ${action} as inscrições do campeonato "${t.name}"?`)) return;
    setBusy(true);
    try {
      await tournamentService.update(t.id, {
        ...t,
        starts_at: dt(t.starts_at),
        ends_at: dt(t.ends_at),
        registration_starts_at: dt(t.registration_starts_at) || null,
        registration_ends_at: dt(t.registration_ends_at) || null,
        status: active ? 'registration' : 'draft'
      });
      await load();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const uploadImage = async file => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await uploadService.upload(file);
      setForm(x => ({ ...x, image_url: r.data.url }));
    } finally {
      setBusy(false);
    }
  };

  const filteredClients = useMemo(() => opts.clients.filter(c => {
    const q = query.toLowerCase();
    return !q || c.name.toLowerCase().includes(q) || String(c.nickname || '').toLowerCase().includes(q);
  }).slice(0, 30), [opts.clients, query]);

  const saveTeam = async () => {
    setBusy(true);
    try {
      const data = { ...teamForm.data, player_ids: teamForm.data.player_ids || [] };
      if (teamForm.data.id) await tournamentService.updateTeam(teamForm.tournamentId, teamForm.data.id, data);
      else await tournamentService.addTeam(teamForm.tournamentId, data);
      setTeamForm(null);
      setQuery('');
      await load();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const deleteTeam = async (t, x) => {
    if (!window.confirm(`Apagar a equipe "${x.name}"?\n\nOs jogadores serão removidos desta inscrição.`)) return;
    setBusy(true);
    try {
      await tournamentService.deleteTeam(t.id, x.id);
      await load();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const updatePaymentStatus = async (tournamentId, teamId, payment_status) => {
    try {
      await tournamentService.payment(tournamentId, teamId, payment_status);
      await load();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    }
  };

  const generateBracket = async t => {
    if (!window.confirm(`Gerar o chaveamento com as equipes confirmadas para "${t.name}"?`)) return;
    setBusy(true);
    try {
      await tournamentService.bracket(t.id);
      await load();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const awardChampion = async () => {
    if (!awardForm?.team_id) return;
    setBusy(true);
    try {
      await tournamentService.awardChampion(awardForm.tournamentId, awardForm);
      setAwardForm(null);
      await load();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const editMatch = async (t, m) => {
    const start = prompt('Início do confronto (AAAA-MM-DDTHH:MM)', dt(m.scheduled_start));
    if (start === null) return;
    const end = prompt('Término do confronto (AAAA-MM-DDTHH:MM)', dt(m.scheduled_end));
    if (end === null) return;
    let status = m.status, a = m.team1_score, b = m.team2_score;
    if (m.team1_id && m.team2_id && window.confirm('Deseja registrar o resultado agora?')) {
      a = Number(prompt(`Placar para ${m.team1_name}`, m.team1_score) || 0);
      b = Number(prompt(`Placar para ${m.team2_name}`, m.team2_score) || 0);
      status = 'finished';
    }
    try {
      await tournamentService.updateMatch(t.id, m.id, {
        scheduled_start: start || null,
        scheduled_end: end || null,
        team1_score: a,
        team2_score: b,
        status
      });
      await load();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    }
  };

  return (
    <div style={s.page}>
      <style>{`
        @media(max-width: 900px) {
          .t-two-cols { grid-template-columns: 1fr !important; }
          .t-poster-col { max-width: 100% !important; min-height: 280px !important; }
        }
      `}</style>

      {/* Main Page Header */}
      <header style={s.head}>
        <div>
          <small style={s.eye}>ARENA COMPETITIVA</small>
          <h1 style={s.h}>Campeonatos</h1>
          <p style={s.muted}>Inscrições, equipes, pagamentos, datas e chaveamento em um só lugar.</p>
        </div>
        <button style={s.primary} onClick={() => setForm({ ...empty })}>+ Novo campeonato</button>
      </header>

      {/* MULTI-TOURNAMENT LIST BLOCKS */}
      <div style={s.tournamentListStack}>
        {list.length === 0 ? (
          <div style={s.emptyStateCard}>
            <span style={{ fontSize: 42 }}>🏆</span>
            <strong style={{ fontSize: 18, color: '#fff', marginTop: 10 }}>Nenhum campeonato cadastrado</strong>
            <p style={{ color: '#8b8b9e', margin: '4px 0 16px' }}>Clique no botão acima para criar o primeiro campeonato da arena.</p>
            <button style={s.primary} onClick={() => setForm({ ...empty })}>+ Criar Primeiro Campeonato</button>
          </div>
        ) : (
          list.map(t => {
            const rounds = [...new Set((t.matches || []).map(m => m.round_number))];
            const publicLink = `${window.location.origin}/campeonato/${t.slug}`;

            return (
              <article className="tournament-card-block" style={s.tBlockCard} key={t.id}>

                {/* 1. TOP SECTION: TWO COLUMNS (IMAGE + INFO) */}
                <div className="t-two-cols" style={s.tTwoCols}>
                  
                  {/* Left Column: Image / Poster */}
                  <div className="t-poster-col" style={s.posterCol}>
                    {t.image_url ? (
                      <img src={t.image_url} alt={t.name} style={s.posterImg} />
                    ) : (
                      <div style={s.posterFallback}>
                        <span style={{ fontSize: 40 }}>🎮</span>
                        <small style={s.noCoverText}>ARTE DE DIVULGAÇÃO</small>
                      </div>
                    )}
                  </div>

                  {/* Right Column: Information & Actions */}
                  <div style={s.infoCol}>
                    {/* Header Bar inside card */}
                    <div style={s.topActionsRow}>
                      <span style={{ ...s.badge, ...(t.status === 'registration' ? s.badgeOpen : {}) }}>
                        {statusLabel[t.status] || t.status}
                      </span>

                      <div style={s.actionBtnsGroup}>
                        {t.status === 'draft' && (
                          <button style={s.openRegistration} disabled={busy} onClick={() => toggleRegistrations(t, true)}>
                            ● ABRIR INSCRIÇÕES
                          </button>
                        )}
                        {t.status === 'registration' && (
                          <button style={s.closeRegistration} disabled={busy} onClick={() => toggleRegistrations(t, false)}>
                            ○ DESATIVAR INSCRIÇÕES
                          </button>
                        )}
                        <button
                          style={s.btn}
                          onClick={() => navigator.clipboard.writeText(publicLink).then(() => alert('Link copiado!'))}
                        >
                          📋 Copiar link
                        </button>
                        <button
                          style={s.btn}
                          onClick={() => setForm({
                            ...t,
                            starts_at: dt(t.starts_at),
                            ends_at: dt(t.ends_at),
                            registration_starts_at: dt(t.registration_starts_at),
                            registration_ends_at: dt(t.registration_ends_at)
                          })}
                        >
                          ✏️ Editar
                        </button>
                        {t.award ? (
                          <div style={s.awarded}>
                            🏆 {t.award.champion_team_name}
                            <small>Pontuação lançada</small>
                          </div>
                        ) : (
                          <button style={s.scoreBtn} onClick={() => setAwardForm({ tournamentId: t.id, team_id: '', notes: '' })}>
                            🏆 Pontuação
                          </button>
                        )}
                        <button
                          style={s.primary}
                          onClick={() => setTeamForm({ tournamentId: t.id, data: { name: '', clan_id: '', captain_client_id: '', player_ids: [] } })}
                        >
                          + Equipe
                        </button>
                      </div>
                    </div>

                    {/* Title & Game Subtitle */}
                    <h2 style={s.tTitle}>{t.name}</h2>
                    <div style={s.gameSub}>
                      {t.game_name} · {t.category_name || 'COMPUTADORES'} · <b>{t.team_limit} equipes</b>
                    </div>
                    <a style={s.publicLink} href={publicLink} target="_blank" rel="noreferrer">
                      🔗 {publicLink}
                    </a>

                    {/* Dispute Rules & Premiação Box */}
                    <div style={s.rulesPrizesBox}>
                      <div style={s.rulesCol}>
                        <strong style={s.subBoxTitle}>Modelo de disputa</strong>
                        <div style={s.rulesText}>
                          Primeiras fases <b>MD{t.early_best_of}</b> · Semifinal <b>MD{t.semifinal_best_of}</b> · Final <b>MD{t.final_best_of}</b> · 3º lugar <b>{t.third_place_enabled ? `MD${t.third_place_best_of}` : 'Não'}</b>
                        </div>
                      </div>

                      <div style={s.prizesCol}>
                        <strong style={s.subBoxTitle}>Premiação</strong>
                        <div style={s.prizesRowInline}>
                          <span>🥇 <b>{t.prize_first || 'A definir'}</b></span>
                          <span>🥈 <b>{t.prize_second || 'A definir'}</b></span>
                          <span>🥉 <b>{t.prize_third || 'A definir'}</b></span>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

                {/* 2. MIDDLE SECTION (INSIDE THE SAME DIV): EQUIPES & GERAR CHAVEAMENTO */}
                <div style={s.middleSection}>
                  <div style={s.sectionHeader}>
                    <h3 style={s.secTitle}>Equipes inscritas</h3>
                    <b style={s.accent}>{(t.teams || []).length}/{t.team_limit} equipes</b>
                  </div>

                  {(t.teams || []).length === 0 ? (
                    <div style={s.emptyTeamsMsg}>
                      Nenhuma equipe inscrita neste campeonato até o momento.
                    </div>
                  ) : (
                    <div style={s.teamsListGrid}>
                      {t.teams.map(x => (
                        <div style={s.teamCardRow} key={x.id}>
                          <div>
                            <strong style={{ color: '#fff', fontSize: 15 }}>{x.name}</strong>
                            <small style={s.block}>{x.clan_name || 'Sem clã'} · Capitão: {x.captain_nickname ? `@${x.captain_nickname}` : x.captain_name}</small>
                            <small style={s.block}>{(x.players || []).map(p => p.nickname ? `@${p.nickname}` : p.name).join(', ')}</small>
                          </div>
                          <div style={s.teamControls}>
                            <select
                              style={s.smallSelect}
                              value={x.payment_status}
                              onChange={e => updatePaymentStatus(t.id, x.id, e.target.value)}
                            >
                              <option value="pending">Pagamento pendente</option>
                              <option value="paid">Pago</option>
                              <option value="waived">Isento</option>
                              <option value="refunded">Reembolsado</option>
                            </select>
                            <button
                              style={s.editTeamBtn}
                              title="Editar equipe"
                              onClick={() => setTeamForm({
                                tournamentId: t.id,
                                data: {
                                  id: x.id,
                                  name: x.name,
                                  clan_id: x.clan_id || '',
                                  captain_client_id: x.captain_client_id,
                                  player_ids: x.players.map(p => Number(p.client_id))
                                }
                              })}
                            >
                              ✎ Editar
                            </button>
                            <button
                              style={s.deleteTeamBtn}
                              title="Apagar equipe"
                              disabled={busy}
                              onClick={() => deleteTeam(t, x)}
                            >
                              ⌫
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* GERAR CHAVEAMENTO BUTTON (INSIDE THE SAME DIV BELOW TEAMS) */}
                  <button
                    style={s.gerarChaveamentoBigBtn}
                    disabled={busy}
                    onClick={() => generateBracket(t)}
                  >
                    ⚡ GERAR CHAVEAMENTO DA COMPETIÇÃO
                  </button>
                </div>

                {/* 3. BOTTOM SECTION (INSIDE THE SAME DIV): CHAVEAMENTO E DATAS */}
                {rounds.length > 0 && (
                  <div style={s.bottomSection}>
                    <div style={s.sectionHeader}>
                      <h3 style={s.secTitle}>Chaveamento e datas</h3>
                      <small style={s.muted}>Clique no confronto para definir data ou placar.</small>
                    </div>
                    <div style={s.bracketContainer}>
                      {rounds.map(r => (
                        <div style={s.roundColumn} key={r}>
                          <h4 style={s.roundHeaderName}>{t.matches.find(m => m.round_number === r)?.round_name}</h4>
                          {t.matches.filter(m => m.round_number === r).map(m => (
                            <button style={s.matchBtnCard} key={m.id} onClick={() => editMatch(t, m)}>
                              <small style={{ color: '#8b8b9e', fontSize: 10 }}>
                                {m.scheduled_start ? new Date(m.scheduled_start).toLocaleString('pt-BR') : 'Definir data'}
                              </small>
                              <div style={s.matchTeamRow}>
                                <span>{m.team1_name || 'A definir'}</span>
                                <b style={{ color: m.winner_team_id && m.winner_team_id === m.team1_id ? '#a3e635' : '#fff' }}>{m.team1_score}</b>
                              </div>
                              <div style={s.matchTeamRow}>
                                <span>{m.team2_name || 'A definir'}</span>
                                <b style={{ color: m.winner_team_id && m.winner_team_id === m.team2_id ? '#a3e635' : '#fff' }}>{m.team2_score}</b>
                              </div>
                              <span style={{ color: '#a3e635', fontSize: 10, marginTop: 2, display: 'block' }}>
                                MD{m.best_of} · {m.status}
                              </span>
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </article>
            );
          })
        )}
      </div>

      {/* CREATE / EDIT TOURNAMENT MODAL */}
      {form && (
        <Modal title={form.id ? 'Editar campeonato' : 'Novo campeonato'} close={() => setForm(null)}>
          <div style={s.formGrid}>
            <Field label="Nome">
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label="Status das inscrições">
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                {!['draft', 'registration'].includes(form.status) && (
                  <option value={form.status}>{statusLabel[form.status] || form.status}</option>
                )}
                <option value="draft">Desativadas · Rascunho</option>
                <option value="registration">Ativas · Inscrições abertas</option>
              </select>
            </Field>
            <Field label="Jogo">
              <select value={form.game_id} onChange={e => setForm({ ...form, game_id: e.target.value })}>
                <option value="">Selecione</option>
                {opts.games.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
            </Field>
            <Field label="Device / categoria">
              <select value={form.package_category_id || ''} onChange={e => setForm({ ...form, package_category_id: e.target.value })}>
                <option value="">Selecione</option>
                {opts.categories.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
            </Field>
            <Field label="Times">
              <select value={form.team_limit} onChange={e => setForm({ ...form, team_limit: Number(e.target.value) })}>
                {[2, 4, 8, 16, 32].map(x => <option key={x} value={x}>{x}</option>)}
              </select>
            </Field>
            <Field label="Jogadores por time">
              <input type="number" min="1" value={form.players_per_team} onChange={e => setForm({ ...form, players_per_team: Number(e.target.value) })} />
            </Field>
            <Field label="Reservas">
              <input type="number" min="0" value={form.reserve_players} onChange={e => setForm({ ...form, reserve_players: Number(e.target.value) })} />
            </Field>
            <Field label="Valor por equipe">
              <input type="number" step="0.01" value={form.registration_fee} onChange={e => setForm({ ...form, registration_fee: e.target.value })} />
            </Field>

            <div style={s.periodSectionTitle}>PERÍODO DAS INSCRIÇÕES</div>
            <Field label="Início das inscrições">
              <input type="datetime-local" value={form.registration_starts_at || ''} onChange={e => setForm({ ...form, registration_starts_at: e.target.value })} />
            </Field>
            <Field label="Fim das inscrições">
              <input type="datetime-local" value={form.registration_ends_at || ''} onChange={e => setForm({ ...form, registration_ends_at: e.target.value })} />
            </Field>

            <div style={s.periodSectionTitle}>PERÍODO DO CAMPEONATO</div>
            <Field label="Início do campeonato">
              <input type="datetime-local" value={form.starts_at} onChange={e => setForm({ ...form, starts_at: e.target.value })} />
            </Field>
            <Field label="Término do campeonato">
              <input type="datetime-local" value={form.ends_at} onChange={e => setForm({ ...form, ends_at: e.target.value })} />
            </Field>

            {[
              ['Primeiras fases', 'early_best_of'],
              ['Semifinal', 'semifinal_best_of'],
              ['Final', 'final_best_of'],
              ['Terceiro lugar', 'third_place_best_of']
            ].map(([l, k]) => (
              <Field label={l} key={k}>
                <select value={form[k]} onChange={e => setForm({ ...form, [k]: Number(e.target.value) })}>
                  {[1, 3, 5, 7].map(x => <option key={x} value={x}>MD{x}</option>)}
                </select>
              </Field>
            ))}

            <Field label="1º prêmio">
              <input value={form.prize_first || ''} onChange={e => setForm({ ...form, prize_first: e.target.value })} />
            </Field>
            <Field label="2º prêmio">
              <input value={form.prize_second || ''} onChange={e => setForm({ ...form, prize_second: e.target.value })} />
            </Field>
            <Field label="3º prêmio">
              <input value={form.prize_third || ''} onChange={e => setForm({ ...form, prize_third: e.target.value })} />
            </Field>

            <Field label="Imagem de divulgação">
              <input type="file" accept="image/*" onChange={e => uploadImage(e.target.files?.[0])} />
            </Field>
          </div>

          {form.image_url && <img src={form.image_url} alt="" style={{ width: 180, borderRadius: 8, marginTop: 12 }} />}
          <button style={{ ...s.primary, width: '100%', marginTop: 18 }} disabled={busy} onClick={saveTournament}>
            {busy ? 'Salvando...' : 'Salvar campeonato'}
          </button>
        </Modal>
      )}

      {/* CREATE / EDIT TEAM MODAL */}
      {teamForm && (
        <Modal title={teamForm.data.id ? 'Editar equipe' : 'Adicionar equipe'} close={() => setTeamForm(null)}>
          <Field label="Nome da equipe">
            <input value={teamForm.data.name} onChange={e => setTeamForm({ ...teamForm, data: { ...teamForm.data, name: e.target.value } })} />
          </Field>
          <Field label="Clã">
            <select value={teamForm.data.clan_id} onChange={e => setTeamForm({ ...teamForm, data: { ...teamForm.data, clan_id: e.target.value } })}>
              <option value="">Sem clã</option>
              {opts.clans.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
          </Field>
          <input
            style={s.search}
            placeholder="Pesquisar cliente por nome ou nick"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <div style={s.peopleList}>
            {filteredClients.map(c => {
              const checked = (teamForm.data.player_ids || []).includes(c.id);
              return (
                <label style={checked ? s.personOn : s.person} key={c.id}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => {
                      const player_ids = checked
                        ? teamForm.data.player_ids.filter(x => x !== c.id)
                        : [...(teamForm.data.player_ids || []), c.id];
                      setTeamForm({
                        ...teamForm,
                        data: {
                          ...teamForm.data,
                          player_ids,
                          captain_client_id: checked && Number(teamForm.data.captain_client_id) === c.id
                            ? (player_ids[0] || '')
                            : teamForm.data.captain_client_id || (checked ? '' : c.id)
                        }
                      });
                    }}
                  />
                  <span>
                    <b>{c.nickname ? `@${c.nickname}` : c.name}</b>
                    <small style={{ display: 'block', color: '#8b8b9e' }}>{c.name}</small>
                  </span>
                  <input
                    type="radio"
                    name="cap"
                    checked={Number(teamForm.data.captain_client_id) === c.id}
                    onChange={() => setTeamForm({
                      ...teamForm,
                      data: {
                        ...teamForm.data,
                        captain_client_id: c.id,
                        player_ids: [...new Set([...(teamForm.data.player_ids || []), c.id])]
                      }
                    })}
                  />
                </label>
              );
            })}
          </div>
          <small style={s.muted}>Marque os integrantes e use o círculo à direita para escolher o capitão.</small>
          <button
            style={{ ...s.primary, width: '100%', marginTop: 14 }}
            disabled={busy || !teamForm.data.name || !teamForm.data.captain_client_id}
            onClick={saveTeam}
          >
            {busy ? 'Salvando...' : teamForm.data.id ? 'Salvar alterações' : 'Adicionar equipe'}
          </button>
        </Modal>
      )}

      {/* GAMIFICATION / CHAMPION AWARD MODAL */}
      {awardForm && (() => {
        const currentT = list.find(x => x.id === awardForm.tournamentId);
        const champion = currentT?.teams?.find(x => Number(x.id) === Number(awardForm.team_id));
        return (
          <Modal title="🏆 Confirmar campeão e pontuar" close={() => setAwardForm(null)}>
            <p style={s.muted}>O lançamento é definitivo e ficará registrado no histórico de gamificação.</p>
            <Field label="Equipe campeã">
              <select value={awardForm.team_id} onChange={e => setAwardForm({ ...awardForm, team_id: e.target.value })}>
                <option value="">Selecione</option>
                {(currentT?.teams || []).filter(x => x.status !== 'cancelled').map(x => (
                  <option key={x.id} value={x.id}>{x.name} · {x.clan_name || 'sem clã'}</option>
                ))}
              </select>
            </Field>

            {champion && (
              <div style={s.awardPreview}>
                <h3 style={{ margin: '0 0 10px', color: '#a3e635' }}>{champion.name}</h3>
                <div style={s.awardNumbers}>
                  <span><b>+100 pts</b> para cada jogador</span>
                  <span><b>+500 pts</b> para o clã</span>
                  <span>🥇 Medalhas pessoais</span>
                  <span>🏆 Troféu institucional</span>
                </div>
                <div style={s.playerPreview}>
                  {(champion.players || []).map(p => (
                    <span key={p.client_id} style={s.playerBadge}>
                      {p.nickname ? `@${p.nickname}` : p.name} <b>+100</b>
                    </span>
                  ))}
                </div>
                <strong style={{ color: '#bef264', display: 'block', marginTop: 8 }}>
                  Total do clã neste título: +{500 + ((champion.players || []).length * 100)} pts
                </strong>
              </div>
            )}

            <Field label="Observação">
              <textarea
                rows="3"
                value={awardForm.notes || ''}
                onChange={e => setAwardForm({ ...awardForm, notes: e.target.value })}
              />
            </Field>

            <button
              style={{ ...s.scoreBtn, width: '100%', marginTop: 16 }}
              disabled={busy || !champion}
              onClick={awardChampion}
            >
              {busy ? 'Lançando...' : 'Confirmar resultado e pontuar'}
            </button>
          </Modal>
        );
      })()}

    </div>
  );
}

const Field = ({ label, children }) => (
  <label style={s.field}>
    <span style={s.fieldLabel}>{label}</span>
    {React.cloneElement(children, { style: { ...s.control, ...(children.props.style || {}) } })}
  </label>
);

const Modal = ({ title, close, children }) => (
  <div style={s.overlay}>
    <div style={s.modal}>
      <div style={s.panelHead}>
        <h2 style={s.mTitle}>{title}</h2>
        <button style={s.xBtn} onClick={close}>×</button>
      </div>
      {children}
    </div>
  </div>
);

const s = {
  page: { color: '#fff', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 20, marginBottom: 24 },
  eye: { color: '#a3e635', fontWeight: 900, letterSpacing: 2, fontSize: 11 },
  h: { margin: '4px 0', fontSize: 30, fontWeight: 900 },
  muted: { color: '#8b8b9e', margin: '4px 0', fontSize: 13 },
  accent: { color: '#a3e635' },
  primary: { border: 0, borderRadius: 8, padding: '11px 18px', background: '#a3e635', color: '#09090e', fontWeight: 900, cursor: 'pointer', fontSize: 13 },
  scoreBtn: { border: '1px solid #a3e635', borderRadius: 8, padding: '10px 14px', background: '#1c2e07', color: '#d9f99d', fontWeight: 900, cursor: 'pointer', fontSize: 12 },
  openRegistration: { border: '1px solid #65a30d', borderRadius: 8, padding: '10px 14px', background: '#a3e635', color: '#09090e', fontWeight: 900, cursor: 'pointer', fontSize: 12 },
  closeRegistration: { border: '1px solid #be123c', borderRadius: 8, padding: '10px 14px', background: '#3b111b', color: '#fda4af', fontWeight: 900, cursor: 'pointer', fontSize: 12 },
  awarded: { display: 'flex', flexDirection: 'column', padding: '6px 11px', border: '1px solid #65a30d', borderRadius: 8, color: '#d9f99d', fontWeight: 900, fontSize: 12, background: '#122008' },
  btn: { border: '1px solid #3b3b4b', borderRadius: 8, padding: '10px 14px', background: '#191925', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 12 },

  /* Stack of Tournament Cards */
  tournamentListStack: { display: 'flex', flexDirection: 'column', gap: 28 },
  emptyStateCard: { background: '#14141f', border: '1px solid #30303f', borderRadius: 16, padding: '48px 24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' },

  /* Single Tournament Card Block */
  tBlockCard: { background: '#14141f', border: '1px solid #2e2e42', borderRadius: 16, padding: 22, boxShadow: '0 10px 30px rgba(0,0,0,0.4)', display: 'flex', flexDirection: 'column', gap: 20 },
  
  /* Top Section: 2 Columns (Image + Info) */
  tTwoCols: { display: 'grid', gridTemplateColumns: 'minmax(200px, 260px) 1fr', gap: 22, alignItems: 'stretch' },
  posterCol: { background: '#0b0b12', borderRadius: 12, border: '1px solid #2a2a3c', overflow: 'hidden', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  posterImg: { width: '100%', height: '100%', minHeight: 220, objectFit: 'cover' },
  posterFallback: { width: '100%', height: '100%', minHeight: 220, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(180deg, #181826, #0e0e18)' },
  noCoverText: { color: '#66667a', fontSize: 10, fontWeight: 900, letterSpacing: 1, marginTop: 6 },
  
  infoCol: { display: 'flex', flexDirection: 'column', gap: 10, justifyContent: 'space-between' },
  topActionsRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 },
  badge: { fontSize: 10, textTransform: 'uppercase', color: '#09090e', background: '#71717a', borderRadius: 20, padding: '4px 9px', fontWeight: 900 },
  badgeOpen: { background: '#a3e635', color: '#08080d' },
  actionBtnsGroup: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  tTitle: { margin: '2px 0 0', color: '#fff', fontSize: 24, fontWeight: 900, textTransform: 'uppercase' },
  gameSub: { fontSize: 13, color: '#a3e635', fontWeight: 800 },
  publicLink: { color: '#a3e635', fontSize: 12, textDecoration: 'none', wordBreak: 'break-all', opacity: 0.9 },

  /* Rules & Prizes Grid */
  rulesPrizesBox: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, background: '#0c0c14', border: '1px solid #222234', padding: 14, borderRadius: 10, marginTop: 4 },
  rulesCol: { display: 'flex', flexDirection: 'column', gap: 4 },
  prizesCol: { display: 'flex', flexDirection: 'column', gap: 4 },
  subBoxTitle: { color: '#8b8b9e', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 900 },
  rulesText: { color: '#ddd', fontSize: 12, lineHeight: 1.4 },
  prizesRowInline: { display: 'flex', gap: 12, color: '#fff', fontSize: 12, flexWrap: 'wrap' },

  /* Middle Section: Equipes & Gerar Chaveamento */
  middleSection: { background: '#0e0e18', border: '1px solid #222236', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 14 },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  secTitle: { margin: 0, fontSize: 16, fontWeight: 900, color: '#fff' },
  emptyTeamsMsg: { padding: '16px 0', color: '#77778a', fontSize: 13, fontStyle: 'italic' },
  teamsListGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 10 },
  teamCardRow: { background: '#141422', border: '1px solid #28283a', borderRadius: 9, padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 },
  teamControls: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  block: { display: 'block', color: '#8b8b9e', marginTop: 2, fontSize: 11 },
  smallSelect: { background: '#0b0b12', color: '#fff', border: '1px solid #3c3c50', borderRadius: 7, padding: '6px 8px', fontSize: 12 },
  editTeamBtn: { border: '1px solid #4d7c0f', borderRadius: 7, padding: '6px 10px', background: '#1b2b0d', color: '#bef264', fontWeight: 800, cursor: 'pointer', fontSize: 11 },
  deleteTeamBtn: { width: 30, height: 30, border: '1px solid #7f1d1d', borderRadius: 7, background: '#2a1015', color: '#fb7185', cursor: 'pointer', fontSize: 13 },
  gerarChaveamentoBigBtn: { width: '100%', padding: '14px', background: '#a3e635', color: '#08080d', border: 0, borderRadius: 10, fontWeight: 900, fontSize: 14, letterSpacing: 0.5, cursor: 'pointer', boxShadow: '0 4px 20px rgba(163, 230, 53, 0.2)' },

  /* Bottom Section: Bracket */
  bottomSection: { background: '#090910', border: '1px solid #1f1f2e', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 },
  bracketContainer: { display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 6 },
  roundColumn: { minWidth: 210, display: 'flex', flexDirection: 'column', gap: 10 },
  roundHeaderName: { margin: '0 0 4px', color: '#a3e635', fontSize: 13, fontWeight: 900 },
  matchBtnCard: { display: 'flex', flexDirection: 'column', gap: 4, textAlign: 'left', background: '#12121e', border: '1px solid #28283a', color: '#fff', padding: 10, borderRadius: 9, cursor: 'pointer' },
  matchTeamRow: { display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#fff' },

  /* Modals & Forms */
  overlay: { position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.85)', display: 'grid', placeItems: 'center', padding: 15 },
  modal: { width: 740, maxWidth: '94vw', maxHeight: '90vh', overflow: 'auto', background: 'linear-gradient(180deg,#191925,#12121b)', border: '1px solid #3c3c50', borderRadius: 16, padding: 24, boxShadow: '0 24px 80px #000' },
  panelHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 14 },
  mTitle: { margin: 0, fontSize: 18, color: '#fff', fontWeight: 900 },
  xBtn: { border: 0, background: 'none', color: '#fff', fontSize: 25, cursor: 'pointer' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: '14px 16px', marginTop: 14 },
  periodSectionTitle: { gridColumn: '1/-1', padding: '8px 12px', borderLeft: '3px solid #a3e635', background: '#18220e', color: '#bef264', fontSize: 11, fontWeight: 900, letterSpacing: 1 },
  field: { display: 'grid', gap: 6, color: '#a1a1b2', fontSize: 12, minWidth: 0 },
  fieldLabel: { fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.7, color: '#b9b9c8' },
  control: { width: '100%', minHeight: 44, boxSizing: 'border-box', padding: '10px 12px', background: '#0b0b12', color: '#f4f4f5', border: '1px solid #414152', borderRadius: 8, fontSize: 13, outline: 'none', colorScheme: 'dark' },
  search: { width: '100%', boxSizing: 'border-box', minHeight: 44, padding: '10px 12px', background: '#0b0b12', color: '#fff', border: '1px solid #414152', borderRadius: 8, margin: '14px 0 10px', fontSize: 13, outline: 'none' },
  peopleList: { maxHeight: 300, overflow: 'auto', display: 'grid', gap: 6, paddingRight: 4 },
  person: { display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 10, alignItems: 'center', padding: '10px 12px', border: '1px solid #292938', borderRadius: 8 },
  personOn: { display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 10, alignItems: 'center', padding: '10px 12px', border: '1px solid #a3e635', borderRadius: 8, background: '#1a2e05' },
  awardPreview: { margin: '14px 0', padding: 14, border: '1px solid #4d7c0f', background: '#18230d', borderRadius: 10 },
  awardNumbers: { display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 6, color: '#d9f99d', fontSize: 12 },
  playerPreview: { display: 'flex', flexWrap: 'wrap', gap: 6, margin: '10px 0' },
  playerBadge: { background: '#253e0d', color: '#bef264', padding: '4px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700 }
};
